<template>
  <div class="bg-white rounded-xl border border-gray-200/60">
    <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
      <div class="min-w-0">
        <h2 class="text-[14px] font-semibold text-gray-900 truncate">{{ title }}</h2>
        <p v-if="subtitle" class="text-[12px] text-gray-400 mt-0.5 truncate">{{ subtitle }}</p>
      </div>
      <span v-if="!loading && rows.length" class="text-[12px] font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-2.5 py-0.5 rounded">
        {{ formatNumber(total) }}
      </span>
    </div>
    <div class="p-4">
      <LoadingBlock v-if="loading" height="200" label="Loading..." />
      <ul v-else-if="rows.length" class="space-y-2">
        <li v-for="row in rows" :key="row.id" class="flex items-center gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between mb-1">
              <span class="text-[13px] text-gray-700 truncate">{{ row.label }}</span>
              <span class="text-[12px] font-data text-gray-500 ml-2 shrink-0">{{ formatNumber(row.count) }}</span>
            </div>
            <!-- Width-relative bar — visual proxy for share of total.
                 Computed against the leader so the largest row reads
                 100% wide; subsequent rows scale relative to that. -->
            <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                class="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300"
                :style="{ width: pct(row.count) + '%' }"
              ></div>
            </div>
          </div>
        </li>
      </ul>
      <div v-else class="py-8 text-center text-[13px] text-gray-400">{{ emptyLabel }}</div>
    </div>
  </div>
</template>

<script>
import LoadingBlock from '../../../components/LoadingBlock.vue'

/**
 * Compact ranked-list card used across the overview for every Top-N
 * breakdown (countries, regions, cities, shows, categories,
 * referrers, embed hosts, languages, age range). Renders the rows as
 * label + count + a thin progress bar scaled to the leader, which
 * conveys share-of-total without the visual weight of a real bar
 * chart. Donut-style breakdowns (devices, OS, browsers, gender) use
 * `DonutCard.vue` instead — different shape, similar role.
 *
 * Inputs are kept as plain `[{ id, label, count }]` so the parent
 * stays in control of mapping API field names. No business logic here.
 */
export default {
  name: 'BreakdownCard',
  components: { LoadingBlock },
  props: {
    title:      { type: String, required: true },
    subtitle:   { type: String, default: '' },
    loading:    { type: Boolean, default: false },
    rows:       { type: Array, default: () => [] },
    emptyLabel: { type: String, default: 'No data' }
  },
  computed: {
    total() {
      return this.rows.reduce((sum, r) => sum + (r.count || 0), 0)
    },
    leader() {
      return this.rows.reduce((max, r) => Math.max(max, r.count || 0), 0)
    }
  },
  methods: {
    pct(count) {
      if (!this.leader) return 0
      return Math.min(100, Math.round((count / this.leader) * 100))
    },
    formatNumber(n) {
      if (n == null || isNaN(n)) return '—'
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
      if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
      return Number(n).toLocaleString()
    }
  }
}
</script>
